import { useEffect, useRef, useCallback, useState } from 'react';

const DRAFT_PREFIX = 'roofclaw_intake_draft_';

/**
 * Auto-save and restore form state via localStorage.
 * Works with uncontrolled forms (native FormData).
 *
 * @param {string} token - Portal token (used as localStorage key suffix)
 * @param {React.RefObject} formRef - Ref to the <form> element
 * @param {object} options
 * @param {number} options.debounceMs - Debounce delay for auto-save (default: 500)
 * @param {boolean} options.disabled - Disable auto-save (e.g. when form is locked)
 * @returns {{ draftRestored: boolean, draftSavedAt: Date|null, clearDraft: () => void }}
 */
export default function useFormDraft(token, formRef, { debounceMs = 500, disabled = false } = {}) {
    const [draftRestored, setDraftRestored] = useState(false);
    const [draftSavedAt, setDraftSavedAt] = useState(null);
    const timerRef = useRef(null);
    const storageKey = `${DRAFT_PREFIX}${token}`;

    // Restore draft on mount
    useEffect(() => {
        if (disabled || !token || !formRef.current) return;

        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return;

            const draft = JSON.parse(raw);
            if (!draft || typeof draft.fields !== 'object') return;

            const form = formRef.current;
            const fields = draft.fields;

            // Restore each field value
            Object.entries(fields).forEach(([name, value]) => {
                const elements = form.elements[name];
                if (!elements) return;

                // Handle radio buttons (NodeList)
                if (elements instanceof RadioNodeList) {
                    elements.value = value;
                    return;
                }

                // Handle single elements (input, select, textarea)
                if (elements.tagName) {
                    if (elements.type === 'checkbox') {
                        elements.checked = value === 'on' || value === true;
                    } else {
                        elements.value = value;
                    }
                }
            });

            setDraftRestored(true);
            // Auto-dismiss the restored indicator after 4 seconds
            setTimeout(() => setDraftRestored(false), 4000);
        } catch (err) {
            console.warn('Failed to restore form draft:', err);
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Save draft (debounced)
    const saveDraft = useCallback(() => {
        if (disabled || !token || !formRef.current) return;

        if (timerRef.current) clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            try {
                const form = formRef.current;
                if (!form) return;

                const fd = new FormData(form);
                const fields = {};
                for (const [key, val] of fd.entries()) {
                    // Skip password/secret fields from draft
                    if (key.startsWith('skill_key_') || key === 'crm_api_token') continue;
                    fields[key] = val;
                }

                // Don't save if all fields are empty
                const hasContent = Object.values(fields).some(v => v && v.trim());
                if (!hasContent) return;

                const draft = {
                    fields,
                    savedAt: new Date().toISOString(),
                    token,
                };
                localStorage.setItem(storageKey, JSON.stringify(draft));
                setDraftSavedAt(new Date());
            } catch (err) {
                console.warn('Failed to save form draft:', err);
            }
        }, debounceMs);
    }, [token, formRef, debounceMs, disabled, storageKey]);

    // Attach input event listener for auto-save
    useEffect(() => {
        if (disabled || !formRef.current) return;

        const form = formRef.current;
        form.addEventListener('input', saveDraft);
        form.addEventListener('change', saveDraft); // For selects and radios

        return () => {
            form.removeEventListener('input', saveDraft);
            form.removeEventListener('change', saveDraft);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [saveDraft, disabled, formRef]);

    // Clear draft (call on successful submission)
    const clearDraft = useCallback(() => {
        try {
            localStorage.removeItem(storageKey);
            setDraftSavedAt(null);
        } catch (err) {
            console.warn('Failed to clear form draft:', err);
        }
    }, [storageKey]);

    return { draftRestored, draftSavedAt, clearDraft };
}
